import { useEffect, useState } from 'react';
import { api, errorText } from '../lib/api';

export function CampaignSettings({ campaignId }: { campaignId: string }) {
  const [s, setS] = useState<any>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const load = async () => {
    try {
      setS(await api(`/api/campaigns/${campaignId}/settings`));
    } catch (e) {
      setErr(errorText(e));
    }
  };
  useEffect(() => {
    void load();
  }, [campaignId]);
  if (!s) return <div className="page">{err ? <p className="error">{err}</p> : <p className="muted">Loading…</p>}</div>;
  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    setErr('');
    setMsg('');
    try {
      await fn();
      if (ok) setMsg(ok);
      await load();
    } catch (x) {
      setErr(errorText(x));
    }
  };
  return (
    <div className="page narrow">
      <h1>Campaign settings</h1>
      {err && <p className="error">{err}</p>}
      {msg && <p className="ok">{msg}</p>}
      <CampaignForm c={s.campaign} onSave={(body) => act(() => api(`/api/campaigns/${campaignId}`, { method: 'PATCH', body }), 'Saved')} />

      <section className="card">
        <h2>People at the table</h2>
        <p className="muted small">Players (including you). Link player characters to players on each PC’s record.</p>
        <ul className="plain">
          {s.players.map((p: any) => (
            <li key={p.id} className="row">
              <span className="grow">
                {p.name} {p.is_me ? <span className="tag subtle">me</span> : null} {p.is_gm ? <span className="tag subtle">GM</span> : null}
                {s.pcs.filter((pc: any) => pc.player_id === p.id).map((pc: any) => (
                  <span key={pc.id} className="muted small"> · plays {pc.name}</span>
                ))}
              </span>
              <button
                className="link small"
                onClick={() => {
                  const name = prompt('Name', p.name);
                  if (name) void act(() => api(`/api/campaigns/${campaignId}/players/${p.id}`, { method: 'PUT', body: { name, isGm: !!p.is_gm } }));
                }}
              >
                Rename
              </button>
              {!p.is_me && (
                <button className="link small" onClick={() => void act(() => api(`/api/campaigns/${campaignId}/players/${p.id}`, { method: 'DELETE' }))}>
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
        <AddPlayer onAdd={(name, isGm) => act(() => api(`/api/campaigns/${campaignId}/players`, { body: { name, isGm } }))} />
      </section>

      <section className="card">
        <h2>Note prefixes</h2>
        <p className="muted small">
          Start a note with a configured prefix, e.g. <code>Jack Notes:</code>, to record whose perspective it is, or <code>Global Notes:</code> for campaign-wide information. A prefix can also set visibility (useful for a public “Table:” prefix in GM campaigns).
        </p>
        <table className="table">
          <thead>
            <tr>
              <th>Prefix</th>
              <th>Meaning</th>
              <th>Player</th>
              <th>Visibility</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {s.prefixes.map((p: any) => (
              <tr key={p.id}>
                <td>
                  <code>{p.label}:</code>
                </td>
                <td>{p.scope === 'player' ? 'Player perspective' : p.scope === 'global' ? 'Global campaign info' : 'GM note'}</td>
                <td>{s.players.find((x: any) => x.id === p.player_id)?.name ?? '—'}</td>
                <td>{p.visibility ? (p.visibility === 'public' ? 'Public' : 'GM only') : 'Default'}</td>
                <td>
                  <button className="link small" onClick={() => void act(() => api(`/api/campaigns/${campaignId}/prefixes/${p.id}`, { method: 'DELETE' }))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <AddPrefix players={s.players} onAdd={(body) => act(() => api(`/api/campaigns/${campaignId}/prefixes`, { body }))} />
      </section>
    </div>
  );
}

function CampaignForm({ c, onSave }: { c: any; onSave: (b: any) => void }) {
  const [f, setF] = useState({ name: c.name, gameSystem: c.game_system, edition: c.edition, myRole: c.my_role, defaultVisibility: c.default_visibility, description: c.description, archived: !!c.archived });
  return (
    <form
      className="card form-grid"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(f);
      }}
    >
      <label className="span2">
        Name
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
      </label>
      <label>
        Game system
        <input value={f.gameSystem} onChange={(e) => setF({ ...f, gameSystem: e.target.value })} />
      </label>
      <label>
        Edition
        <input value={f.edition} onChange={(e) => setF({ ...f, edition: e.target.value })} />
      </label>
      <label>
        My role
        <select value={f.myRole} onChange={(e) => setF({ ...f, myRole: e.target.value })}>
          <option value="player">Player</option>
          <option value="gm">GM</option>
        </select>
      </label>
      <label>
        New notes are
        <select value={f.defaultVisibility} onChange={(e) => setF({ ...f, defaultVisibility: e.target.value })}>
          <option value="public">Public</option>
          <option value="gm_only">GM only</option>
        </select>
      </label>
      <label className="span2">
        Description
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} />
      </label>
      <label className="check span2">
        <input type="checkbox" checked={f.archived} onChange={(e) => setF({ ...f, archived: e.target.checked })} /> Archived
      </label>
      <div className="span2">
        <button className="btn primary">Save campaign</button>
      </div>
    </form>
  );
}

function AddPlayer({ onAdd }: { onAdd: (name: string, isGm: boolean) => Promise<void> }) {
  const [name, setName] = useState('');
  const [gm, setGm] = useState(false);
  return (
    <form
      className="row wrap"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) void onAdd(name.trim(), gm).then(() => setName(''));
      }}
    >
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Player name" aria-label="Player name" />
      <label className="check">
        <input type="checkbox" checked={gm} onChange={(e) => setGm(e.target.checked)} /> GM
      </label>
      <button className="btn small">Add player</button>
    </form>
  );
}

function AddPrefix({ players, onAdd }: { players: any[]; onAdd: (b: any) => Promise<void> }) {
  const [f, setF] = useState({ label: '', scope: 'player', playerId: '', visibility: '' });
  return (
    <form
      className="row wrap"
      onSubmit={(e) => {
        e.preventDefault();
        void onAdd({ ...f, playerId: f.playerId || null, visibility: f.visibility || null }).then(() => setF({ ...f, label: '' }));
      }}
    >
      <input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="e.g. Jack Notes" aria-label="Prefix label" required />
      <select value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} aria-label="Meaning">
        <option value="player">Player perspective</option>
        <option value="global">Global info</option>
        <option value="gm">GM note</option>
      </select>
      {f.scope === 'player' && (
        <select value={f.playerId} onChange={(e) => setF({ ...f, playerId: e.target.value })} aria-label="Player">
          <option value="">Player…</option>
          {players.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      )}
      <select value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value })} aria-label="Visibility">
        <option value="">Default visibility</option>
        <option value="public">Public</option>
        <option value="gm_only">GM only</option>
      </select>
      <button className="btn small">Add prefix</button>
    </form>
  );
}
